import { Box, Flex } from '@chakra-ui/react';
import { Button, useToast } from '@morressier/alembic';
import { useRequestRevision } from 'src/api/commands';
import { IDecisionModalProps } from './decisionModalHelper';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import ReviewSummaryAccordion from './ReviewSummaries';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import { useWorkflow } from 'src/hooks/useWorkflow';
import _ from 'lodash';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { JSONSchemaType } from 'ajv';
import {
  FormFieldDefinition,
  FormFieldRadio,
  FormFieldTextInput
} from '@publishing-engine/domain/types/article';
import {
  CustomFormFieldComponent,
  FormFieldRadioGroup
} from 'src/components/FormField';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import { Overlay } from '../../Overlay';
import StepHeader from '../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import ComposeDecisionEmail, { EmailContentForm } from './ComposeDecisionEmail';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { useSteps } from './hooks';
import AdjudicatorReviewSummary from './AdjudicatorReviewSummary';
import { buildEmailPayload } from '../../../utils/normalizeEmailRecipients';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { useAttachmentSync } from '../../../hooks/useAttachmentSync';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import DecisionLayout from './DecisionLayout';
import ResponseToAuthorField from './ResponseToAuthorField';
import ModerateCOCComment from './ModerateCOCComment';
import RecommendationRounds from './RecommendationRounds';
import { useQueryClient } from '@tanstack/react-query';

export const RequestRevisionModal = createEditorialModal(
  ({
    onClose,
    submissionId,
    submissionContextId,
    organizationId
  }: IDecisionModalProps) => {
    const {
      forms: { editorRevisionRequest },
      articleWorkflow
    } = useWorkflow();
    const queryClient = useQueryClient();

    if (!editorRevisionRequest) {
      return <></>;
    }

    const toast = useToast();
    const { recommedationSummary } = useCOCContextData();
    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      commandName: 'RequestRevision'
    });
    const { currentStep, goToNextStep, goToPreviousStep } = useSteps(
      hasCustomizableTemplates ? 2 : 0
    );

    const {
      refetchSubmission,
      reviewsSummary,
      adjudicatorReviewsSummary,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    useEffect(() => {
      queryClient.invalidateQueries({
        queryKey: ['reviews-summary', submissionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendation-summary', submissionId]
      });
    }, []);

    const { formFields, schema } = editorRevisionRequest;
    const defaultValues: Record<string, unknown> = {};
    const fields: Record<string, FormFieldDefinition> = {};
    formFields?.forEach(field => {
      fields[field.name] = field;
      _.set(defaultValues, field.caseDataFields.main, undefined);
    });
    const methods = useForm({
      defaultValues,
      resolver: ajvResolver(schema as JSONSchemaType<unknown>)
    });

    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);
    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const revisionSeverity = methods.watch('revisionSeverity') as
      | string
      | undefined;

    const requestRevisionMutation = useRequestRevision(
      submissionId,
      submissionContextId,
      articleWorkflow.id,
      refetchSubmission,
      organizationId,
      buildEmailPayload(emailContent)
    );

    const requestRevision = async (formValues: any) => {
      const latestRecommendationCycle =
        recommedationSummary?.recommendationRounds?.at(-1)?.cycle?.at(-1);

      const COCcomments = latestRecommendationCycle?.participants
        .map(
          item =>
            (item?.recommendation?.moderatedCommentForAuthor ?? '') ||
            (item?.recommendation?.commentForAuthor ?? '')
        )
        .filter(Boolean)
        .join(' \n');
      const editorComment = COCcomments
        ? `${formValues?.editorComment ?? ''}\n${COCcomments}`
        : formValues?.editorComment ?? '';

      const attachments = latestRecommendationCycle?.participants
        ?.flatMap(participant => {
          const rec = participant?.recommendation;
          const source = rec?.moderatedAttachments?.length
            ? rec.moderatedAttachments
            : rec?.attachments ?? [];
          return source;
        })
        .filter(attachment => attachment?.visibility === 'show');

      const updatedFormValues = {
        ...formValues,
        editorComment: editorComment,
        editorAttachments: attachments
      };

      const res = await requestRevisionMutation.mutateAsync(updatedFormValues);
      if (res.isSuccess) {
        await submitEditorNote(editorNote);
        toast({
          variant: 'success',
          title: 'Submission In Revision',
          description: 'The author has been invited to revise this Submission.'
        });
        onClose();
      } else {
        toast({
          variant: 'error',
          title: 'Error requesting revision',
          description:
            'There was an error requesting revision on the submission.'
        });
      }
    };

    const onSubmitFirstStep: SubmitHandler<any> = (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        methods.handleSubmit(requestRevision)();
      }
    };

    const onSubmitEmailStep = () => {
      methods.handleSubmit(requestRevision)();
    };

    // useEffect(() => {
    //   const savedComment = localStorage.getItem(
    //     `${submissionId}.revisionRequest.editorComment`
    //   );
    //   if (savedComment) {
    //     methods.reset({ editorComment: savedComment });
    //   }
    // }, [submissionId, methods.reset]);

    const renderDecisionSidebar = () => <RecommendationRounds fullWidth />;

    const renderFirstStep = () => (
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmitFirstStep)}
          style={{ width: '100%' }}
        >
          <StepHeader
            currentStep={1}
            totalSteps={hasCustomizableTemplates ? 2 : 0}
            heading="Invite to Revise"
            description="Proceeding with this decision confirms you have reviewed the
                    submission against the provided Reviews. The author will be
                    informed of this decision along with the following
                    information:"
            marginY={2}
          />

          <Box paddingBottom={8} width="100%">
            <Flex flexDirection="column" gap={4}>
              {fields?.RevisionSeverity && (
                <FormFieldRadioGroup
                  config={fields.RevisionSeverity as FormFieldRadio}
                />
              )}
              {fields?.EditorComment && (
                <>
                  <ResponseToAuthorField
                    submissionId={submissionId}
                    decisionType="RequestRevision"
                    subtypeSelected={revisionSeverity}
                  />
                  <CustomFormFieldComponent
                    config={fields.EditorComment as FormFieldTextInput}
                    placeholder="Add feedback to the author"
                  />
                </>
              )}
              {recommedationSummary?.recommendationRounds && (
                <ModerateCOCComment
                  RecommendationRound={
                    recommedationSummary.recommendationRounds
                  }
                  submissionContextId={submissionContextId}
                />
              )}
              <Flex direction="column" mb={3}>
                <ReviewSummaryAccordion
                  reviews={reviewsSummary ?? []}
                  submissionId={submissionContextId}
                />
                <AdjudicatorReviewSummary
                  adjudicatorReview={adjudicatorReviewsSummary ?? []}
                />
              </Flex>
              <EditorialNote value={editorNote} onChange={setEditorNote} />
            </Flex>
          </Box>
        </form>
      </FormProvider>
    );

    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form
          onSubmit={emailContentMethods.handleSubmit(onSubmitEmailStep)}
          style={{ width: '100%' }}
        >
          {' '}
          <StepHeader
            currentStep={2}
            totalSteps={hasCustomizableTemplates ? 2 : 0}
            heading="Invite to Revise - Send Email Notification"
            description="Compose an email to notify the author about the revision request."
            marginY={2}
          />
          <ComposeDecisionEmail
            organizationId={organizationId}
            commandName="RequestRevision"
            subtype={revisionSeverity}
            revisionSeverity={revisionSeverity}
            formPayload={methods.getValues()}
          />
          <div style={{ display: 'none' }}>
            <EditorialNote value={editorNote} onChange={setEditorNote} />
          </div>
        </form>
      </FormProvider>
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={() => methods.handleSubmit(requestRevision)()}
                isLoading={
                  !hasCustomizableTemplates && requestRevisionMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() =>
              emailContentMethods.handleSubmit(onSubmitEmailStep)()
            }
            isLoading={requestRevisionMutation.isPending}
          >
            {'Submit Decision'}
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout
          onClose={onClose}
          manuscriptId={manuscriptId}
          sidebar={currentStep === 1 ? renderDecisionSidebar() : undefined}
        >
          {currentStep === 1
            ? renderFirstStep()
            : hasCustomizableTemplates
            ? renderEmailStep()
            : null}
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
